/**
 * IdentifyResultsPanel — QGIS-style Identify Results dock
 * Shows attributes of clicked/selected features, grouped by layer,
 * with direct inline attribute field editing and field save/edit buttons.
 */
import React, { useCallback, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Button,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  Close,
  ExpandMore,
  LocationOn,
  MyLocation,
  ClearAll,
  Edit,
  Check,
  Add,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { clearSelection } from '../store/slices/selectionSlice';
import { updateFeatureAttribute } from '../store/slices/layersSlice';
import { addNotification } from '../store/slices/uiSlice';
import { mapNavigator } from '../services/MapNavigator';
import { useGeoveraTheme } from '../theme/ThemeContext';

interface IdentifyResultsPanelProps {
  open: boolean;
  onClose: () => void;
  identifyMode?: boolean;
}

function parseVal(input: string, originalVal?: any): any {
  if (typeof originalVal === 'number') {
    const num = Number(input);
    return isNaN(num) ? input : num;
  }
  if (typeof originalVal === 'boolean') {
    return input.toLowerCase() === 'true';
  }
  if (!isNaN(Number(input)) && input.trim() !== '') {
    return Number(input);
  }
  if (input.toLowerCase() === 'true' || input.toLowerCase() === 'false') {
    return input.toLowerCase() === 'true';
  }
  return input;
}

interface FeatureAttributeCardProps {
  item: { layerId: string; featureId: string; properties?: Record<string, any> };
  enrichProps: Record<string, any>;
  palette: any;
}

const FeatureAttributeCard: React.FC<FeatureAttributeCardProps> = ({
  item,
  enrichProps,
  palette,
}) => {
  const dispatch = useAppDispatch();
  const [editingFields, setEditingFields] = useState<Record<string, string>>({});
  const [addingField, setAddingField] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newVal, setNewVal] = useState('');

  const entries = Object.entries(enrichProps);

  const startEditField = (field: string, currentVal: any) => {
    setEditingFields((prev) => ({
      ...prev,
      [field]: currentVal === null || currentVal === undefined ? '' : String(currentVal),
    }));
  };

  const cancelEditField = (field: string) => {
    setEditingFields((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const saveEditField = (field: string, originalVal: any) => {
    const rawVal = editingFields[field];
    if (rawVal === undefined) return;
    const parsedVal = parseVal(rawVal, originalVal);

    dispatch(
      updateFeatureAttribute({
        layerId: item.layerId,
        featureId: item.featureId,
        field,
        value: parsedVal,
      })
    );

    dispatch(
      addNotification({
        type: 'success',
        message: `Updated "${field}" = "${parsedVal}"`,
      })
    );

    cancelEditField(field);
  };

  const handleAddField = () => {
    if (!newKey.trim()) return;
    const parsedVal = parseVal(newVal);

    dispatch(
      updateFeatureAttribute({
        layerId: item.layerId,
        featureId: item.featureId,
        field: newKey.trim(),
        value: parsedVal,
      })
    );

    dispatch(
      addNotification({
        type: 'success',
        message: `Added attribute field "${newKey.trim()}"`,
      })
    );

    setNewKey('');
    setNewVal('');
    setAddingField(false);
  };

  return (
    <Box
      sx={{
        mb: 1.5,
        border: `1px solid ${palette.border.subtle}`,
        borderRadius: 1.5,
        p: 1.25,
        bgcolor: palette.bg.paper,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: palette.text.muted }}>
          Feature: {item.featureId || '—'}
        </Typography>
        <Button
          size="small"
          startIcon={<Add fontSize="small" />}
          onClick={() => setAddingField(!addingField)}
          sx={{ fontSize: 11, py: 0, textTransform: 'none' }}
        >
          Add Field
        </Button>
      </Box>

      {entries.length === 0 ? (
        <Typography variant="body2" sx={{ color: palette.text.muted }}>
          No attributes
        </Typography>
      ) : (
        <Table
          size="small"
          sx={{
            '& td': {
              py: 0.5,
              px: 0.5,
              fontSize: 12,
              borderBottom: `1px solid ${palette.border.subtle}`,
            },
          }}
        >
          <TableBody>
            {entries.map(([k, v]) => {
              const isEditing = k in editingFields;
              return (
                <TableRow key={k}>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: palette.text.muted,
                      width: '32%',
                      verticalAlign: 'middle',
                    }}
                  >
                    {k}
                  </TableCell>
                  <TableCell sx={{ color: palette.text.primary, verticalAlign: 'middle' }}>
                    {isEditing ? (
                      <TextField
                        size="small"
                        fullWidth
                        variant="outlined"
                        value={editingFields[k]}
                        onChange={(e) =>
                          setEditingFields((prev) => ({ ...prev, [k]: e.target.value }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEditField(k, v);
                          if (e.key === 'Escape') cancelEditField(k);
                        }}
                        sx={{
                          '& .MuiInputBase-input': { py: 0.25, px: 0.75, fontSize: 12 },
                        }}
                        autoFocus
                      />
                    ) : v === null || v === undefined ? (
                      '—'
                    ) : (
                      String(v)
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ width: 68, verticalAlign: 'middle' }}>
                    {isEditing ? (
                      <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                        <Tooltip title="Save field">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => saveEditField(k, v)}
                            sx={{ p: 0.25 }}
                          >
                            <Check fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => cancelEditField(k)}
                            sx={{ p: 0.25 }}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ) : (
                      <Tooltip title="Edit field">
                        <IconButton
                          size="small"
                          onClick={() => startEditField(k, v)}
                          sx={{ p: 0.25, color: palette.accent.primary }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {addingField && (
        <Box sx={{ mt: 1, p: 1, border: `1px dashed ${palette.border.base}`, borderRadius: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            New Attribute Field
          </Typography>
          <Stack spacing={0.75}>
            <TextField
              size="small"
              placeholder="Field Name (e.g. Owner)"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              sx={{ '& .MuiInputBase-input': { py: 0.25, px: 0.75, fontSize: 12 } }}
            />
            <TextField
              size="small"
              placeholder="Initial Value"
              value={newVal}
              onChange={(e) => setNewVal(e.target.value)}
              sx={{ '& .MuiInputBase-input': { py: 0.25, px: 0.75, fontSize: 12 } }}
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="small" onClick={() => setAddingField(false)}>
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<Check fontSize="small" />}
                onClick={handleAddField}
                disabled={!newKey.trim()}
              >
                Save Field
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

const IdentifyResultsPanel: React.FC<IdentifyResultsPanelProps> = ({
  open,
  onClose,
  identifyMode = false,
}) => {
  const dispatch = useAppDispatch();
  const { palette } = useGeoveraTheme();
  const selectedFeatures = useAppSelector((s) => s.selection.selectedFeatures);
  const layers = useAppSelector((s) => s.layers.layers);

  const handleClear = useCallback(() => {
    dispatch(clearSelection());
  }, [dispatch]);

  const zoomToSelected = useCallback(() => {
    if (selectedFeatures.length === 0) return;
    mapNavigator.zoomToSelection(selectedFeatures, layers);
  }, [selectedFeatures, layers]);

  if (!open) return null;

  const grouped = selectedFeatures.reduce((acc: Record<string, typeof selectedFeatures>, sf) => {
    const layer = layers.find((l) => l.id === sf.layerId);
    const layerName = layer?.name || sf.layerId;
    if (!acc[layerName]) acc[layerName] = [];
    acc[layerName].push(sf);
    return acc;
  }, {});

  const layerNames = Object.keys(grouped);

  // Enrich properties from layer features if selection only has ids
  const enrich = (item: (typeof selectedFeatures)[0]) => {
    const layer = layers.find((l) => l.id === item.layerId);
    const feature = layer?.features?.find((f) => f.id === item.featureId);
    if (feature?.properties && Object.keys(feature.properties).length > 0)
      return feature.properties;
    if (item.properties && Object.keys(item.properties).length > 0) return item.properties;
    return {};
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        right: 12,
        top: 60,
        width: 380,
        maxHeight: '75vh',
        overflow: 'auto',
        bgcolor: palette.bg.elevated,
        color: palette.text.primary,
        border: `1px solid ${palette.border.base}`,
        borderRadius: 2,
        boxShadow: 6,
        zIndex: 1300,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1,
          pl: 1.5,
          bgcolor: identifyMode ? 'rgba(26,115,232,0.15)' : palette.bg.highest,
          borderBottom: `1px solid ${palette.border.subtle}`,
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}
      >
        <LocationOn fontSize="small" sx={{ mr: 1, color: palette.accent.primary }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Identify
            {selectedFeatures.length > 0 ? ` (${selectedFeatures.length})` : ''}
          </Typography>
          {identifyMode && (
            <Typography variant="caption" sx={{ color: palette.accent.primary, fontWeight: 600 }}>
              Mode on — click map features to inspect & edit
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose} title="Close" aria-label="Close identify">
          <Close fontSize="small" />
        </IconButton>
      </Box>

      {selectedFeatures.length === 0 ? (
        <Box sx={{ p: 2 }}>
          <Typography
            variant="body2"
            sx={{ color: palette.text.secondary, mb: 1.5, lineHeight: 1.55 }}
          >
            Click a feature on the map to inspect & edit attributes directly. Hold Ctrl and click,
            or drag a box, to select multiple.
          </Typography>
          <Chip
            size="small"
            label={identifyMode ? 'Identify & Edit mode active' : 'Selection viewer'}
            color={identifyMode ? 'primary' : 'default'}
          />
        </Box>
      ) : (
        <>
          <Stack direction="row" spacing={0.75} sx={{ p: 1, px: 1.25 }}>
            <Button
              size="small"
              startIcon={<MyLocation sx={{ fontSize: 16 }} />}
              onClick={zoomToSelected}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Zoom to
            </Button>
            <Button
              size="small"
              startIcon={<ClearAll sx={{ fontSize: 16 }} />}
              onClick={handleClear}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Clear
            </Button>
          </Stack>
          <Divider />
          <Box>
            {layerNames.map((ln) => (
              <Accordion key={ln} defaultExpanded disableGutters elevation={0}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {ln}
                    </Typography>
                    <Chip size="small" label={grouped[ln].length} />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                  {grouped[ln].map((item, i) => {
                    const props = enrich(item);
                    return (
                      <FeatureAttributeCard
                        key={`${item.layerId}-${item.featureId}-${i}`}
                        item={item}
                        enrichProps={props}
                        palette={palette}
                      />
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default IdentifyResultsPanel;
